import styled from 'styled-components';
import { screenSm } from 'constants/styles/breakpoints';
import { ModalActions, ModalContent, ModalContainer } from 'components/modal/styles';

export const VoucherModalContainer = styled(ModalContainer)`
  && {
    height: auto;
    min-height: auto;
    max-height: calc(100vh - 100px);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
`;

export const Content = styled(ModalContent)`
  && {
    padding: 12px 30px 0 30px;
    margin: 0;
  }
  gap: 0;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0; /* Important for flex children to shrink properly */
  overflow: hidden;

  @media (max-width: 768px) {
    && {
      padding: 20px 16px 0 16px;
    }
  }
`;

export const SearchLabel = styled.label`
  padding: 0 0 10px 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--primary-darker, #111a27);
  display: block;
`;

export const UserListContainer = styled.div`
  flex: 1;
  min-height: 150px;
  max-height: 350px;
  overflow-y: auto;
  padding: 0;
  background: transparent;
  margin-top: 10px;
  /* Ensure scrollbar doesn't cause layout shift */
  scrollbar-gutter: stable;
`;

export const EmptyStateText = styled.div`
  text-align: center;
  color: var(--darker-gray, #57575c);
  padding: 16px 8px;
  font-size: 14px;
`;

export const ModalActionsSection = styled(ModalActions)`
  && {
    padding: 16px 30px;
  }
  flex-direction: column-reverse;

  ${screenSm} {
    flex-direction: row;
  }
`;

export const AssignmentSummary = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  color: var(--light-gray-darker, #57575c);
  font-size: 14px;
  font-weight: 500;
  min-height: 20px;
  margin-bottom: 8px;

  ${screenSm} {
    margin-bottom: 0;
  }
`;

export const LoadingOverlay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 150px;
  width: 100%;
  margin-top: 100px;
`;

export const OutHeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  text-align: center;
  color: var(--white);
  width: 100%;
  height: 100%;
  padding: 0 30px;
`;

export const OutHeaderTitle = styled.h2`
  font-family: var(--font-family-base);
  font-size: 24px;
  font-weight: 450;
  margin: 0;
  color: var(--white);
`;

export const OutHeaderSub = styled.div`
  font-family: var(--font-family-base);
  font-size: 16px;
  font-weight: 900;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  a {
    text-decoration: underline;
    color: inherit;
  }
`;

export const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid var(--grey-lighter, #f2f2f7);
  gap: 12px;

  &:last-child {
    border-bottom: none;
  }
`;

export const LeftSide = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  flex: 1;
  min-width: 0;

  .user-row__checkbox > span:not(.ant-checkbox) {
    display: none;
  }
`;

export const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const UserName = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: var(--black-darkest, #111a27);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const UserEmail = styled.div`
  font-size: 12px;
  color: var(--light-gray-darker, #57575c);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const VoucherSelector = styled.div`
  width: 220px;
  flex-shrink: 0;
  position: relative;
`;

export const VoucherDropdownButton = styled.button`
  width: 100%;
  padding: 0 10px;
  background: var(--grey-lighter, #f2f2f7);
  border-radius: 10px;
  border: none;
  height: 44px;
  font-size: 16px;
  font-weight: 400;
  color: var(--icon-default, #57575c);
  text-align: left;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: none;

  &:hover {
    background: var(--grey-lighter, #f2f2f7);
  }

  &:focus {
    outline: none;
    border: 1px solid var(--purple-light, #a793f6);
    box-shadow: 0px 0px 4px 0px var(--black-25, rgba(0, 0, 0, 0.25));
  }
`;

export const DropdownArrow = styled.span<{ isOpen: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--icon-default, #57575c);
  margin-left: auto;

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const VoucherDropdownMenu = styled.div<{ isOpen: boolean }>`
  background: var(--white, white);
  border: 1px solid var(--gray-light, #d0d0d0);
  border-radius: 6px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
  max-height: 250px;
  overflow-y: auto;
  padding: 8px;
`;

export const VoucherList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

export const VoucherItem = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 8px;
  border-radius: 0;
  cursor: pointer;

  span,
  .label,
  .checkbox-label {
    font-weight: 700;
  }
  font-weight: 700;
  border-bottom: 1px solid var(--gray-lightest, #f5f5f5);

  label {
    font-weight: 700;
  }

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--gray-lightest, #f9f9f9);
  }
`;

export const VoucherItemLabel = styled.span<{ isEligible: boolean }>`
  opacity: ${({ isEligible }) => (isEligible ? 1 : 0.5)};
  display: flex;
  align-items: center;
`;

export const TooltipWrapper = styled.span`
  display: inline-flex;
  cursor: help;
`;

export const TooltipWrapperWithMargin = styled(TooltipWrapper)`
  margin-left: 8px;
`;

export const VoucherSelectorControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const FailedSummaryText = styled.span`
  color: var(--error, #fe6d6d);
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const tooltipOverlayStyle = {
  textAlign: 'center' as const,
  maxWidth: '250px',
  backgroundColor: 'var(--light-gray-darker, #57575C)',
};

export const ContactSalesLink = styled.a`
  color: inherit;
  text-decoration: underline;
  cursor: pointer;
`;

export const ContactSalesSpan = styled.span`
  text-decoration: underline;
  cursor: pointer;
`;

export const ChevronIconWrapper = styled.span`
  margin-top: 2px;
`;

export const SearchForm = styled.form`
  background-color: var(--grey-lighter, #f2f2f7);
  flex-grow: 1;
  border-radius: 10px;
`;
